import React from 'react';
import {
  TextField,
  TextFieldProps,
  alpha,
  useTheme,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface ModernTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  isPassword?: boolean;
  showPasswordToggle?: boolean;
}

const ModernTextField: React.FC<ModernTextFieldProps> = ({
  isPassword = false,
  showPasswordToggle = false,
  ...props
}) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <TextField
      {...props}
      type={isPassword && !showPassword ? 'password' : 'text'}
      variant="outlined"
      fullWidth
      InputProps={{
        endAdornment: showPasswordToggle && isPassword ? (
          <InputAdornment position="end">
            <IconButton
              onClick={handleTogglePassword}
              edge="end"
              size="small"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          '& fieldset': {
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '&:hover fieldset': {
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          },
          '&.Mui-focused fieldset': {
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
          '&.Mui-error fieldset': {
            border: `2px solid ${theme.palette.error.main}`,
            boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.1)}`,
          }
        },
        '& .MuiInputLabel-root': {
          fontWeight: 500,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          }
        },
        '& .MuiOutlinedInput-input': {
          padding: '14px 16px',
          fontSize: '16px',
          fontWeight: 400,
        },
        ...props.sx
      }}
    />
  );
};

export default ModernTextField;
